import { useEffect, useMemo, useState, type FormEvent } from "react";
import { errorText } from "../../i18n/serverErrors";
import { api } from "../../api/apiClient";
import type {
  AssignableUser,
  TaskRecurrenceKind,
  TaskSchedule,
  UpdateTaskSchedulePayload,
} from "../../api/types";
import { useT } from "../../i18n/LanguageProvider";
import type { TranslateFn } from "../../i18n/t";
import type { PlainKey } from "../../i18n/types";
import { parseFlexibleTime24 } from "../../utils/time24";
import { TimeInput24 } from "../TimeInput24";
import { appendTaskFormValue, minutesToTime, taskDayKey, timeToMinutes } from "./taskUi";
import { intlLocale } from "../../i18n/languages";
import { compareText } from "../../i18n/intl";

/**
 * The seven day names, from the PLATFORM rather than from thirty-five
 * dictionary keys — the call S4c already makes in `ShopFloorTasksPanel`.
 *
 * A function and not a const: a module-level array would freeze one
 * language at import. 2024-01-07 is a Sunday, so `7 + day` walks Sun→Sat,
 * and UTC keeps the date from sliding a day in either direction.
 */
function weekdayOptions(locale: string): Array<{ value: number; label: string }> {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
  return [0, 1, 2, 3, 4, 5, 6].map((value) => ({
    value,
    label: fmt.format(new Date(Date.UTC(2024, 0, 7 + value))),
  }));
}

interface TaskScheduleEditorProps {
  locationId?: string;
  schedule?: TaskSchedule;
  assignableUsers: AssignableUser[];
  actions?: TaskScheduleActions;
  onSaved: () => Promise<void>;
  onDeleted?: () => Promise<void>;
  onCancel?: () => void;
  onError: (message: string | null) => void;
}

export interface TaskScheduleActions {
  create: (form: FormData) => Promise<TaskSchedule>;
  update: (id: string, payload: UpdateTaskSchedulePayload) => Promise<TaskSchedule>;
  remove: (id: string) => Promise<{ id: string }>;
}

const defaultActions: TaskScheduleActions = {
  create: (form) => api.createTaskSchedule(form),
  update: (id, payload) => api.updateTaskSchedule(id, payload),
  remove: (id) => api.deleteTaskSchedule(id),
};

/**
 * G-85 S4 — a role in words, not a lower-cased Prisma enum.
 *
 * `user.role.toLowerCase()` reads acceptably in English and not at all in
 * Russian; the dictionary already carries these four from S2's hub rail.
 */
function roleWords(t: TranslateFn, role: string): string {
  const keys: Record<string, PlainKey> = {
    OWNER: "role.OWNER",
    ADMIN: "role.ADMIN",
    MANAGER: "role.MANAGER",
    FLORIST: "role.FLORIST",
  };
  const key = keys[role];
  return key ? t(key) : role;
}

export function TaskScheduleEditor({
  locationId,
  schedule,
  assignableUsers,
  actions = defaultActions,
  onSaved,
  onDeleted,
  onCancel,
  onError,
}: TaskScheduleEditorProps) {
  const { t, language } = useT();
  const [name, setName] = useState(schedule?.name ?? "");
  const [description, setDescription] = useState(schedule?.description ?? "");
  const [notes, setNotes] = useState(schedule?.notes ?? "");
  const [assigneeUserId, setAssigneeUserId] = useState(schedule?.assignee?.id ?? "");
  const [recurrenceKind, setRecurrenceKind] = useState<TaskRecurrenceKind>(
    schedule?.recurrenceKind ?? "EVERY_N_DAYS",
  );
  const [intervalDays, setIntervalDays] = useState(String(schedule?.intervalDays ?? 1));
  const [intervalWeeks, setIntervalWeeks] = useState(String(schedule?.intervalWeeks ?? 1));
  const [weekdays, setWeekdays] = useState<number[]>(schedule?.weekdays ?? [1]);
  const [timeOfDay, setTimeOfDay] = useState(minutesToTime(schedule?.timeOfDayMinutes ?? 540));
  const [startDate, setStartDate] = useState(schedule?.startDate ?? taskDayKey(new Date()));
  const [endDate, setEndDate] = useState(schedule?.endDate ?? "");
  const [requireCompletionPhoto, setRequireCompletionPhoto] = useState(
    schedule?.requireCompletionPhoto ?? false,
  );
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(schedule?.name ?? "");
    setDescription(schedule?.description ?? "");
    setNotes(schedule?.notes ?? "");
    setAssigneeUserId(schedule?.assignee?.id ?? "");
    setRecurrenceKind(schedule?.recurrenceKind ?? "EVERY_N_DAYS");
    setIntervalDays(String(schedule?.intervalDays ?? 1));
    setIntervalWeeks(String(schedule?.intervalWeeks ?? 1));
    setWeekdays(schedule?.weekdays ?? [1]);
    setTimeOfDay(minutesToTime(schedule?.timeOfDayMinutes ?? 540));
    setStartDate(schedule?.startDate ?? taskDayKey(new Date()));
    setEndDate(schedule?.endDate ?? "");
    setRequireCompletionPhoto(schedule?.requireCompletionPhoto ?? false);
    setReferenceImage(null);
  }, [schedule]);

  const sortedUsers = useMemo(
    () =>
      [...assignableUsers].sort((a, b) =>
        compareText(`${a.fullName} ${a.role}`, `${b.fullName} ${b.role}`),
      ),
    [assignableUsers],
  );

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    onError(null);
    try {
      const normalizedTime = parseFlexibleTime24(timeOfDay);
      if (!name.trim()) {
        onError(t("tasks.scheduleNameRequired"));
        return;
      }
      if (!normalizedTime) {
        onError(t("tasks.scheduleTimeInvalid"));
        return;
      }
      if (recurrenceKind === "WEEKLY" && weekdays.length === 0) {
        onError(t("tasks.chooseWeekday"));
        return;
      }
      const timeOfDayMinutes = timeToMinutes(normalizedTime);
      const common = {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        notes: notes.trim() ? notes.trim() : null,
        assigneeUserId: assigneeUserId || null,
        recurrenceKind,
        timeOfDayMinutes,
        startDate,
        endDate: endDate || null,
        requireCompletionPhoto,
      };

      if (schedule) {
        await actions.update(schedule.id, {
          ...common,
          isActive: schedule.isActive,
          intervalDays: recurrenceKind === "EVERY_N_DAYS" ? Number(intervalDays) : null,
          intervalWeeks: recurrenceKind === "WEEKLY" ? Number(intervalWeeks) : null,
          weekdays: recurrenceKind === "WEEKLY" ? weekdays : [],
        });
      } else {
        const form = new FormData();
        if (locationId) form.append("locationId", locationId);
        form.append("name", common.name);
        form.append("recurrenceKind", recurrenceKind);
        form.append("timeOfDayMinutes", String(timeOfDayMinutes));
        form.append("startDate", startDate);
        form.append("requireCompletionPhoto", String(requireCompletionPhoto));
        appendTaskFormValue(form, "description", description);
        appendTaskFormValue(form, "notes", notes);
        if (assigneeUserId) form.append("assigneeUserId", assigneeUserId);
        if (endDate) form.append("endDate", endDate);
        if (recurrenceKind === "EVERY_N_DAYS") {
          form.append("intervalDays", intervalDays);
        } else {
          form.append("intervalWeeks", intervalWeeks);
          for (const weekday of weekdays) form.append("weekdays", String(weekday));
        }
        if (referenceImage) form.append("image", referenceImage);
        await actions.create(form);
      }
      await onSaved();
      if (!schedule) {
        setName("");
        setDescription("");
        setNotes("");
        setReferenceImage(null);
      }
    } catch (err: unknown) {
      onError(errorText(err, "tasks.couldNotSaveSchedule"));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    if (!schedule) return;
    setBusy(true);
    onError(null);
    try {
      await actions.update(schedule.id, { isActive: !schedule.isActive });
      await onSaved();
    } catch (err: unknown) {
      onError(errorText(err, "tasks.couldNotUpdateSchedule"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!schedule || !onDeleted) return;
    if (!confirm(t("tasks.deleteSchedulePrompt", { name: schedule.name }))) {
      return;
    }
    setBusy(true);
    onError(null);
    try {
      await actions.remove(schedule.id);
      await onDeleted();
    } catch (err: unknown) {
      onError(errorText(err, "tasks.couldNotDeleteSchedule"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="day-shifts-row" onSubmit={(event) => void submit(event)}>
      <div className="day-shifts-row-head">
        <strong>{schedule ? t("tasks.editRecurring") : t("tasks.createRecurring")}</strong>
        {schedule ? (
          <span className={schedule.isActive ? "badge success" : "badge muted"}>
            {schedule.isActive ? "active" : "paused"}
          </span>
        ) : null}
      </div>
      <div className="modal-form">
        <div className="field full">
          <label htmlFor={schedule ? `schedule-name-${schedule.id}` : "schedule-name-new"}>
            {t("tasks.name")}
          </label>
          <input
            id={schedule ? `schedule-name-${schedule.id}` : "schedule-name-new"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            required
            maxLength={200}
          />
        </div>
        <div className="field full">
          <label htmlFor={schedule ? `schedule-assignee-${schedule.id}` : "schedule-assignee-new"}>
            {t("tasks.assignee")}
          </label>
          <select
            id={schedule ? `schedule-assignee-${schedule.id}` : "schedule-assignee-new"}
            value={assigneeUserId}
            onChange={(e) => setAssigneeUserId(e.target.value)}
            disabled={busy}
          >
            <option value="">{t("taskUi.wholeShop")}</option>
            {sortedUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName} · {roleWords(t, user.role)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={schedule ? `schedule-start-${schedule.id}` : "schedule-start-new"}>
            {t("tasks.startDate")}
          </label>
          <input
            id={schedule ? `schedule-start-${schedule.id}` : "schedule-start-new"}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={busy}
            required
          />
        </div>
        <div className="field">
          <label htmlFor={schedule ? `schedule-end-${schedule.id}` : "schedule-end-new"}>
            {t("tasks.endDate")}
          </label>
          <input
            id={schedule ? `schedule-end-${schedule.id}` : "schedule-end-new"}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>{t("tasks.time")}</label>
          <TimeInput24 value={timeOfDay} onChange={setTimeOfDay} disabled={busy} />
        </div>
        <div className="field">
          <label htmlFor={schedule ? `schedule-kind-${schedule.id}` : "schedule-kind-new"}>
            {t("tasks.repeats")}
          </label>
          <select
            id={schedule ? `schedule-kind-${schedule.id}` : "schedule-kind-new"}
            value={recurrenceKind}
            onChange={(e) => setRecurrenceKind(e.target.value as TaskRecurrenceKind)}
            disabled={busy}
          >
            <option value="EVERY_N_DAYS">{t("tasks.everyNDaysOption")}</option>
            <option value="WEEKLY">{t("tasks.selectedWeekdays")}</option>
          </select>
        </div>
        {recurrenceKind === "EVERY_N_DAYS" ? (
          <div className="field">
            <label htmlFor={schedule ? `schedule-days-${schedule.id}` : "schedule-days-new"}>
              {t("tasks.intervalDays")}
            </label>
            <input
              id={schedule ? `schedule-days-${schedule.id}` : "schedule-days-new"}
              type="number"
              min="1"
              max="365"
              value={intervalDays}
              onChange={(e) => setIntervalDays(e.target.value)}
              disabled={busy}
            />
          </div>
        ) : (
          <>
            <div className="field">
              <label htmlFor={schedule ? `schedule-weeks-${schedule.id}` : "schedule-weeks-new"}>
                {t("tasks.intervalWeeks")}
              </label>
              <input
                id={schedule ? `schedule-weeks-${schedule.id}` : "schedule-weeks-new"}
                type="number"
                min="1"
                max="52"
                value={intervalWeeks}
                onChange={(e) => setIntervalWeeks(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="field full">
              <label>{t("tasks.weekdays")}</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {weekdayOptions(intlLocale(language)).map((day) => (
                  <label
                    key={day.value}
                    className="badge muted"
                    style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
                  >
                    <input
                      type="checkbox"
                      checked={weekdays.includes(day.value)}
                      onChange={(e) => {
                        setWeekdays((current) =>
                          e.target.checked
                            ? [...current, day.value].sort((a, b) => a - b)
                            : current.filter((value) => value !== day.value),
                        );
                      }}
                      disabled={busy}
                    />
                    {day.label}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
        <div className="field full">
          <label htmlFor={schedule ? `schedule-description-${schedule.id}` : "schedule-description-new"}>
            {t("tasks.description")}
          </label>
          <textarea
            id={schedule ? `schedule-description-${schedule.id}` : "schedule-description-new"}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
            rows={2}
          />
        </div>
        <div className="field full">
          <label htmlFor={schedule ? `schedule-notes-${schedule.id}` : "schedule-notes-new"}>
            {t("tasks.notes")}
          </label>
          <textarea
            id={schedule ? `schedule-notes-${schedule.id}` : "schedule-notes-new"}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
            rows={2}
          />
        </div>
        {!schedule ? (
          <div className="field full">
            <label htmlFor="schedule-reference-image-new">{t("tasks.referencePhoto")}</label>
            <input
              id="schedule-reference-image-new"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              onChange={(e) => setReferenceImage(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
          </div>
        ) : null}
        <label
          className="field full"
          style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}
        >
          <input
            type="checkbox"
            checked={requireCompletionPhoto}
            onChange={(e) => setRequireCompletionPhoto(e.target.checked)}
            disabled={busy}
          />
          {t("tasks.requireCompletionPhoto")}
        </label>
      </div>
      <div className="button-row">
        {schedule ? (
          <>
            <button
              type="button"
              className="button plain"
              onClick={() => void toggleActive()}
              disabled={busy}
            >
              {schedule.isActive ? t("tasks.pause") : t("tasks.resume")}
            </button>
            <button
              type="button"
              className="button danger"
              onClick={() => void remove()}
              disabled={busy}
            >
              {t("tasks.delete")}
            </button>
          </>
        ) : null}
        {onCancel ? (
          <button type="button" className="button plain" onClick={onCancel} disabled={busy}>
            {t("common.cancel")}
          </button>
        ) : null}
        <button type="submit" className="button" disabled={busy}>
          {busy ? t("tasks.saving") : schedule ? t("tasks.saveSchedule") : t("tasks.createSchedule")}
        </button>
      </div>
    </form>
  );
}
